import type { BranchKey } from '@festina-life/api'
import { useTranslate } from '@festina-life/ui/hooks'

import { useConciseBranch } from '../../../../uiModels/branches/useConciseBranch'
import { useOperationsContext } from '../../../../workspace/operationsContext'

export const usePushToBaseBranchAvailability = (
  branchKey: BranchKey,
): {
  isPushAvailable: boolean
  isPushVisible: boolean
  unavailableReasons: string[]
} => {
  const translate = useTranslate()
  const { branchValidationErrors } = useOperationsContext()
  const conciseBranch = useConciseBranch({
    server: branchKey.server,
    branchName: branchKey.name,
    shouldFallbackToStandard: false,
  })
  const baseConciseBranch = useConciseBranch({
    server: branchKey.server,
    branchName: conciseBranch?.baseBranchName ?? '',
    shouldFallbackToStandard: false,
  })
  const hasBaseBranch = !!conciseBranch?.baseBranchName && !!baseConciseBranch

  const isCleanOfValidationErrors = branchValidationErrors.size === 0
  const isPointingToTipOfBaseBranch =
    !!conciseBranch &&
    !!baseConciseBranch &&
    conciseBranch.baseChangesetKey === baseConciseBranch.currentChangesetKey
  const doTypesMatch =
    !conciseBranch?.typesVersionIds?.length ||
    (conciseBranch?.typesVersionIds?.length ===
      baseConciseBranch?.typesVersionIds?.length &&
      conciseBranch?.typesVersionIds?.every((typeVersion, index) => {
        const baseTypeVersion = baseConciseBranch?.typesVersionIds?.[index]
        return typeVersion?.typesVersionId === baseTypeVersion?.typesVersionId
      }))
  const hasChangesetsToPush =
    !!conciseBranch &&
    conciseBranch.currentChangesetKey !== conciseBranch.baseChangesetKey
  const hasChanges = hasChangesetsToPush || !doTypesMatch
  const unavailableReasons: string[] = [
    ...(!isPointingToTipOfBaseBranch
      ? [
          translate(
            'branchDetailsPanel.tabs.manage.pushToBaseBranch.disabled.rebaseRequired',
          ),
        ]
      : []),
    ...(!hasChanges
      ? [
          translate(
            'branchDetailsPanel.tabs.manage.pushToBaseBranch.disabled.noChanges',
          ),
        ]
      : []),
    ...(!isCleanOfValidationErrors
      ? [
          translate(
            'branchDetailsPanel.tabs.manage.pushToBaseBranch.disabled.validationError',
          ),
        ]
      : []),
  ]

  return {
    isPushVisible: hasBaseBranch,
    isPushAvailable:
      hasBaseBranch &&
      isCleanOfValidationErrors &&
      isPointingToTipOfBaseBranch &&
      hasChanges,
    unavailableReasons,
  }
}
